import JSZip from 'jszip';
import { CATEGORY_LABELS, LABOR_FIELDS, payrollPeriod, validateLaborRows, type LaborCategory, type LaborRow, type SubmissionRevision } from './types';

const MAIN = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
export interface TemplateEmployee { key: string; category: LaborCategory; row: number; name: string; attendanceOnly?: boolean; }
export interface WorkbookTemplate {
  zip: JSZip; fileName: string; periodKey: string | null;
  sheets: Record<LaborCategory, { path: string; xml: string; document: Document }>;
  employees: TemplateEmployee[]; sharedStrings: string[];
}
export type WorkbookBindings = Record<string, string>;
export interface WorkbookTransfer { category: LaborCategory; cell: string; employeeId: string; field: string; value: number; minutes?: number; }
export interface WorkbookExport { bytes: Uint8Array; transfers: WorkbookTransfer[]; fractionalCells: string[]; clearedCells: string[]; }

export function normalizeEmployeeName(value: string): string { return value.normalize('NFKC').replace(/[\s\u3000]+/g, ''); }
const elements = (node: Document | Element, name: string): Element[] => Array.from(node.getElementsByTagNameNS(MAIN, name));
function parseXml(value: string): Document {
  if (/<!DOCTYPE|<!ENTITY/i.test(value)) throw new Error('このExcelのXML形式には対応していません。');
  const doc = new DOMParser().parseFromString(value, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) throw new Error('ExcelのXMLを読み込めませんでした。');
  return doc;
}
function plainText(node: Element): string {
  return elements(node, 't').filter(t => {
    let parent = t.parentElement;
    while (parent && parent !== node) { if (parent.localName === 'rPh') return false; parent = parent.parentElement; }
    return true;
  }).map(t => t.textContent ?? '').join('');
}
function getCell(doc: Document, ref: string): Element | undefined { return elements(doc, 'c').find(c => c.getAttribute('r') === ref); }
function cellText(cell: Element | undefined, strings: string[]): string {
  if (!cell) return '';
  if (cell.getAttribute('t') === 'inlineStr') return plainText(cell);
  const value = elements(cell, 'v')[0]?.textContent ?? '';
  return cell.getAttribute('t') === 's' ? strings[Number(value)] ?? '' : value;
}
const HEADERS: Record<LaborCategory, Record<string, string>> = {
  fulltime: { C2: '出勤日数', D2: '休出日数/時間', F2: '有給日数', G2: '欠勤日数', H2: '遅刻早退時間', J2: '普残時間', L2: '夜勤～4回', M2: '夜勤5回目～', N2: '夜勤者往復日数', O2: '控除食事代' },
  parttime: { C1: '出勤日数', D1: '休出日数/時間', F1: '有給日数', G1: '勤務時間', I1: '残業時間', K1: '出勤数休出含む', M1: '交通費金額', N1: '控除食事代' },
  night: { D2: '出勤日数', F2: 'うち日勤回数', H2: 'うち夜勤回数', J2: '有給日数', P2: '基本時間', R2: '時間外(×1.25)', T2: '深夜時間(×1.25)', V2: '深夜残業(×1.5)', Y2: '控除食事代' },
};
function normalizeHeader(value: string): string { return normalizeEmployeeName(value).replace(/[〜～]/g, '~'); }
function resolveSheetPath(target: string): string {
  const parts = (target.startsWith('/') ? target.slice(1) : `xl/${target}`).split('/');
  const result: string[] = [];
  for (const p of parts) { if (p === '..') result.pop(); else if (p && p !== '.') result.push(p); }
  const path = result.join('/');
  if (!path.startsWith('xl/worksheets/')) throw new Error('予期しないExcelシート参照です。');
  return path;
}
export function periodFromFileName(name: string): string | null {
  const normalized = name.normalize('NFKC');
  const reiwa = normalized.match(/R(\d{1,2})[.年_-](\d{1,2})(?:月|[】\] ._-]|$)/i);
  const western = normalized.match(/(20\d{2})[年._-](\d{1,2})(?:月|[】\] ._-]|$)/);
  const year = reiwa ? Number(reiwa[1]) + 2018 : western ? Number(western[1]) : 0;
  const month = reiwa ? Number(reiwa[2]) : western ? Number(western[2]) : 0;
  return year && month >= 1 && month <= 12 ? `${year}-${String(month).padStart(2, '0')}` : null;
}

export async function inspectLaborWorkbook(bytes: ArrayBuffer | Uint8Array, fileName: string): Promise<WorkbookTemplate> {
  if (!/\.xlsx$/i.test(fileName) || bytes.byteLength > 10 * 1024 * 1024) throw new Error('10MB以内の .xlsx 原本を選択してください。');
  const zip = await JSZip.loadAsync(bytes);
  if (Object.keys(zip.files).length > 3000) throw new Error('このExcelの構成には対応していません。');
  const read = async (path: string): Promise<string> => {
    const entry = zip.file(path);
    if (!entry) throw new Error(`Excelの必要な構成がありません: ${path}`);
    const value = await entry.async('string');
    if (value.length > 10 * 1024 * 1024) throw new Error('Excelのシートが大きすぎます。');
    return value;
  };
  const workbook = parseXml(await read('xl/workbook.xml'));
  const relationships = parseXml(await read('xl/_rels/workbook.xml.rels'));
  const strings = zip.file('xl/sharedStrings.xml') ? elements(parseXml(await read('xl/sharedStrings.xml')), 'si').map(plainText) : [];
  const sheets = {} as WorkbookTemplate['sheets'];
  const employees: TemplateEmployee[] = [];
  for (const category of Object.keys(CATEGORY_LABELS) as LaborCategory[]) {
    const sheet = elements(workbook, 'sheet').find(s => s.getAttribute('name') === CATEGORY_LABELS[category]);
    if (!sheet) throw new Error(`必要なシート「${CATEGORY_LABELS[category]}」が見つかりません。社労士の入力用原本を選択してください。`);
    const id = sheet.getAttributeNS(REL, 'id');
    const relationship = Array.from(relationships.getElementsByTagName('Relationship')).find(r => r.getAttribute('Id') === id);
    if (!relationship || relationship.getAttribute('TargetMode') === 'External') throw new Error('Excelのシート参照が不正です。');
    const path = resolveSheetPath(relationship.getAttribute('Target') ?? '');
    const xml = await read(path);
    const document = parseXml(xml);
    // Only this known template family is writable. A changed column must be reviewed first.
    for (const [ref, label] of Object.entries(HEADERS[category])) {
      if (normalizeHeader(cellText(getCell(document, ref), strings)) !== normalizeHeader(label)) {
        throw new Error(`${CATEGORY_LABELS[category]} ${ref} の見出しが対応様式と異なります（予定: ${label}）。様式の確認が必要です。`);
      }
    }
    for (const cell of elements(document, 'c')) {
      const match = cell.getAttribute('r')?.match(/^B(\d+)$/);
      if (!match) continue;
      const row = Number(match[1]);
      if (row <= (category === 'parttime' ? 1 : 2)) continue;
      const name = cellText(cell, strings).trim();
      if (!name || /^(合計|総合計|小計)$/.test(normalizeEmployeeName(name))) continue;
      if (elements(cell, 'f').length) throw new Error(`${CATEGORY_LABELS[category]} ${match[0]} の氏名が数式のため照合できません。`);
      // The supplied original has a separate manager block with only an attendance-day cell.
      const attendanceOnly = category === 'fulltime' && !getCell(document, `F${row}`) &&
        normalizeHeader(cellText(getCell(document, `C${row - 1}`), strings)) === normalizeHeader('出勤日数');
      const fields = attendanceOnly ? LABOR_FIELDS[category].filter(f => f.key === 'attendanceDays') : LABOR_FIELDS[category];
      for (const field of fields) {
        const ref = `${field.column}${row + (field.rowOffset ?? 0)}`;
        const input = getCell(document, ref);
        if (!input || elements(input, 'f').length) throw new Error(`${CATEGORY_LABELS[category]} ${ref} が入力欄ではありません。様式の確認が必要です。`);
      }
      employees.push({ key: `${category}:${row}`, category, row, name, ...(attendanceOnly ? { attendanceOnly: true } : {}) });
    }
    if (!employees.some(e => e.category === category)) throw new Error(`${CATEGORY_LABELS[category]} の従業員行が見つかりません。`);
    sheets[category] = { path, xml, document };
  }
  return { zip, fileName, periodKey: periodFromFileName(fileName), sheets, employees, sharedStrings: strings };
}

export function suggestWorkbookBindings(template: WorkbookTemplate, rows: LaborRow[]): WorkbookBindings {
  const result: WorkbookBindings = {};
  for (const target of template.employees) {
    const matches = rows.filter(r => !r.excluded && r.category === target.category && normalizeEmployeeName(r.employeeName) === normalizeEmployeeName(target.name));
    const duplicateTargets = template.employees.filter(e => normalizeEmployeeName(e.name) === normalizeEmployeeName(target.name) && e.category === target.category);
    if (matches.length === 1 && duplicateTargets.length === 1) result[target.key] = matches[0].employeeId;
  }
  return result;
}
export function validateWorkbookBindings(template: WorkbookTemplate, rows: LaborRow[], bindings: WorkbookBindings, skipReasons: Record<string, string> = {}): string[] {
  const issues: string[] = [];
  const assigned = new Set<string>();
  for (const target of template.employees) {
    const id = bindings[target.key];
    if (id === '__skip__') {
      if (!skipReasons[target.key]?.trim()) issues.push(`${CATEGORY_LABELS[target.category]} ${target.name}: 転記しない理由を入力してください。`);
      continue;
    }
    const row = rows.find(r => r.employeeId === id && !r.excluded);
    if (!row) { issues.push(`${CATEGORY_LABELS[target.category]} ${target.name}: 従業員の対応を選択してください。`); continue; }
    if (row.category !== target.category) issues.push(`${target.name}: 確定したシート区分と一致しません。`);
    if (assigned.has(id)) issues.push(`${row.employeeName}: 複数行へ重複して対応しています。`);
    assigned.add(id);
  }
  for (const row of rows.filter(r => !r.excluded)) if (!assigned.has(row.employeeId)) issues.push(`${row.employeeName}（${row.employeeCode || row.employeeId}）: Excel内の対応する行がありません。`);
  return issues;
}

function replaceCellValue(xml: string, ref: string, value: number | null, style?: number): string {
  const pattern = new RegExp(`<c\\b([^>]*\\br="${ref}"[^>]*?)(?:\\/>|>([\\s\\S]*?)<\\/c>)`);
  if (!pattern.test(xml)) throw new Error(`入力セル ${ref} がありません。`);
  return xml.replace(pattern, (_all, attrs: string, content = '') => {
    if (/<f(?:\s|>)/.test(content)) throw new Error(`数式セル ${ref} への転記を中止しました。`);
    let nextAttrs = attrs.replace(/\s+t="[^"]*"/g, '');
    if (style !== undefined) nextAttrs = nextAttrs.replace(/\s+s="[^"]*"/g, '') + ` s="${style}"`;
    const rest = content.replace(/<v(?:\s[^>]*)?>[\s\S]*?<\/v>|<v\s*\/>|<is(?:\s[^>]*)?>[\s\S]*?<\/is>/g, '');
    return `<c${nextAttrs}>${value === null ? '' : `<v>${value}</v>`}${rest}</c>`;
  });
}

function replaceCellStyle(xml: string, ref: string, style: number): string {
  const pattern = new RegExp(`<c\\b([^>]*\\br="${ref}"[^>]*?)(\\/?)>`);
  if (!pattern.test(xml)) throw new Error(`表示セル ${ref} がありません。`);
  return xml.replace(pattern, (_all, attrs: string, close: string) =>
    `<c${attrs.replace(/\s+s="[^"]*"/g, '')} s="${style}"${close}>`);
}

/** Arithmetic parser for the formulas in the supplied template; never execute formula text as code. */
export function recalculateWorksheet(xml: string, strings: string[]): string {
  const doc = parseXml(xml);
  const cells = new Map(elements(doc, 'c').map(c => [c.getAttribute('r')!, c]));
  const values = new Map<string, number>();
  const calculating = new Set<string>();
  const evalCell = (ref: string): number => {
    if (values.has(ref)) return values.get(ref)!;
    if (calculating.has(ref)) throw new Error(`循環する数式があります: ${ref}`);
    calculating.add(ref);
    const cell = cells.get(ref);
    const formula = cell ? elements(cell, 'f')[0] : undefined;
    let result: number;
    if (formula) {
      if (!formula.textContent || formula.hasAttribute('t')) throw new Error(`未対応の数式形式です: ${ref}`);
      result = evalExpression(formula.textContent);
    } else {
      const raw = cellText(cell, strings);
      result = raw === '' ? 0 : Number(raw);
      if (!Number.isFinite(result)) throw new Error(`数式が数値以外のセルを参照しています: ${ref}`);
    }
    if (!Number.isFinite(result)) throw new Error(`数式の計算に失敗しました: ${ref}`);
    calculating.delete(ref); values.set(ref, result); return result;
  };
  const evalExpression = (formula: string): number => {
    const input = formula.replace(/\$/g, '').replace(/\s/g, '').replace(/^=/, '').toUpperCase();
    const tokens = input.match(/SUM|[A-Z]+[1-9]\d*|\d+(?:\.\d+)?|[()+\-*/,:]/g) ?? [];
    if (tokens.join('') !== input) throw new Error(`未対応のExcel数式です: ${formula}`);
    let index = 0;
    const primary = (): number => {
      const token = tokens[index++];
      if (token === '+' || token === '-') return (token === '-' ? -1 : 1) * primary();
      if (token === '(') { const v = expression(); if (tokens[index++] !== ')') throw new Error('数式の括弧が不正です。'); return v; }
      if (token === 'SUM') {
        if (tokens[index++] !== '(') throw new Error('SUM関数が不正です。');
        let sum = 0;
        do {
          const start = tokens[index];
          if (/^[A-Z]+\d+$/.test(start ?? '') && tokens[index + 1] === ':') {
            index += 2; const end = tokens[index++];
            const a = start.match(/^([A-Z]+)(\d+)$/)!; const b = end?.match(/^([A-Z]+)(\d+)$/);
            if (!b || a[1] !== b[1] || Number(b[2]) < Number(a[2]) || Number(b[2]) - Number(a[2]) > 10000) throw new Error('未対応の集計範囲です。');
            for (let row = Number(a[2]); row <= Number(b[2]); row++) sum += evalCell(`${a[1]}${row}`);
          } else sum += expression();
          if (tokens[index] !== ',') break;
          index++;
        } while (index < tokens.length);
        if (tokens[index++] !== ')') throw new Error('SUM関数の終端が不正です。');
        return sum;
      }
      if (/^[A-Z]+\d+$/.test(token ?? '')) return evalCell(token);
      if (/^\d+(?:\.\d+)?$/.test(token ?? '')) return Number(token);
      throw new Error('未対応のExcel数式です。');
    };
    const product = (): number => { let v = primary(); while (tokens[index] === '*' || tokens[index] === '/') { const op = tokens[index++]; const right = primary(); v = op === '*' ? v * right : v / right; } return v; };
    const expression = (): number => { let v = product(); while (tokens[index] === '+' || tokens[index] === '-') { const op = tokens[index++]; const right = product(); v = op === '+' ? v + right : v - right; } return v; };
    const value = expression();
    if (index !== tokens.length) throw new Error('未対応のExcel数式です。');
    return value;
  };
  let result = xml;
  for (const [ref, cell] of Array.from(cells.entries())) {
    if (!elements(cell, 'f').length) continue;
    const value = evalCell(ref);
    const pattern = new RegExp(`(<c\\b[^>]*\\br="${ref}"[^>]*>)([\\s\\S]*?)(<\\/c>)`);
    result = result.replace(pattern, (_all, begin, body: string, end) => `${begin}${body.replace(/<v(?:\s[^>]*)?>[\s\S]*?<\/v>|<v\s*\/>/g, '').replace('</f>', `</f><v>${value}</v>`)}${end}`);
  }
  return result;
}

function fractionalStyles(styles: string, originalStyleIds: number[]): { xml: string; ids: Map<number, number> } {
  const ids = new Map<number, number>();
  if (!originalStyleIds.length) return { xml: styles, ids };
  const match = styles.match(/<cellXfs\b([^>]*)>([\s\S]*?)<\/cellXfs>/);
  if (!match) throw new Error('Excelの表示書式を確認できませんでした。');
  const xfs = match[2].match(/<xf\b[^>]*?(?:\/>|>[\s\S]*?<\/xf>)/g) ?? [];
  const doc = parseXml(styles);
  const numFmtId = Math.max(163, ...elements(doc, 'numFmt').map(e => Number(e.getAttribute('numFmtId')))) + 1;
  const added: string[] = [];
  for (const id of Array.from(new Set(originalStyleIds))) {
    if (!xfs[id]) throw new Error('Excelの入力セルの書式が不正です。');
    ids.set(id, xfs.length + added.length);
    let xf = xfs[id].replace(/\s+numFmtId="[^"]*"/, '').replace(/\s+applyNumberFormat="[^"]*"/, '').replace(/\s+applyAlignment="[^"]*"/, '')
      .replace(/^<xf/, `<xf numFmtId="${numFmtId}" applyNumberFormat="1" applyAlignment="1"`);
    // The original time columns are narrow. Keep their widths and fit the extra decimal digits.
    if (/<alignment\b/.test(xf)) {
      xf = xf.replace(/<alignment\b([^>]*?)(\/?)>/, (_all, attrs: string, close: string) =>
        `<alignment${attrs.replace(/\s+(?:shrinkToFit|wrapText)="[^"]*"/g, '')} shrinkToFit="1" wrapText="0"${close}>`);
    } else {
      const alignment = '<alignment shrinkToFit="1" wrapText="0"/>';
      xf = /\/>$/.test(xf) ? xf.replace(/\/>$/, `>${alignment}</xf>`) : xf.replace('</xf>', `${alignment}</xf>`);
    }
    added.push(xf);
  }
  let xml = styles.replace(match[0], `<cellXfs${match[1].replace(/\s+count="[^"]*"/, '')} count="${xfs.length + added.length}">${match[2]}${added.join('')}</cellXfs>`);
  const fmt = `<numFmt numFmtId="${numFmtId}" formatCode="0.0000"/>`;
  if (/<numFmts\b[^>]*>[\s\S]*?<\/numFmts>/.test(xml)) {
    xml = xml.replace(/<numFmts\b([^>]*)>([\s\S]*?)<\/numFmts>/, (_all, attrs: string, body: string) => `<numFmts${attrs.replace(/\s+count="[^"]*"/, '')} count="${elements(doc, 'numFmt').length + 1}">${body}${fmt}</numFmts>`);
  } else if (/<numFmts\b[^>]*\/>/.test(xml)) {
    xml = xml.replace(/<numFmts\b[^>]*\/>/, `<numFmts count="1">${fmt}</numFmts>`);
  } else xml = xml.replace(/<fonts\b/, `<numFmts count="1">${fmt}</numFmts><fonts`);
  return { xml, ids };
}

export async function exportLaborWorkbook(template: WorkbookTemplate, revision: SubmissionRevision, bindings: WorkbookBindings, skipReasons: Record<string, string> = {}): Promise<WorkbookExport> {
  if (revision.status !== 'confirmed' || revision.closing_day !== 15 || revision.rows.schemaVersion !== 1 || revision.rows.timeEncoding !== 'decimal_hours') throw new Error('確定済みの15日締めデータだけを出力できます。');
  if (template.periodKey && template.periodKey !== revision.period_key) throw new Error('Excel原本の給与月と確定した給与月が一致しません。');
  const period = payrollPeriod(revision.period_key, revision.closing_day);
  if (revision.period_start !== period.start || revision.period_end !== period.end) throw new Error('確定データの給与期間が対象月と一致しません。');
  const rows = revision.rows.employees;
  const issues = [...validateLaborRows(rows), ...validateWorkbookBindings(template, rows, bindings, skipReasons)];
  if (issues.length) throw new Error(issues.slice(0, 8).join('\n'));
  // Clone the package: never mutate the source workbook or a previously exported revision.
  const zip = await JSZip.loadAsync(await template.zip.generateAsync({ type: 'uint8array' }));
  const transfers: WorkbookTransfer[] = [];
  const clearedCells: string[] = [];
  for (const target of template.employees) {
    const row = rows.find(r => r.employeeId === bindings[target.key]);
    if (!row) continue;
    const fields = target.attendanceOnly ? LABOR_FIELDS[row.category].filter(f => f.key === 'attendanceDays') : LABOR_FIELDS[row.category];
    for (const field of fields) {
      const source = row.values[field.key]!;
      if (source === null) throw new Error('未確認の値があります。');
      transfers.push({ category: row.category, cell: `${field.column}${target.row + (field.rowOffset ?? 0)}`,
        employeeId: row.employeeId, field: field.key, value: field.unit === 'minutes' ? source / 60 : source,
        ...(field.unit === 'minutes' ? { minutes: source } : {}) });
    }
  }
  const fractional: { category: LaborCategory; cell: string }[] = transfers.filter(t => t.minutes !== undefined && t.minutes % 60 !== 0);
  const calculatedSheets = {} as Record<LaborCategory, string>;
  for (const category of Object.keys(CATEGORY_LABELS) as LaborCategory[]) {
    const sheet = template.sheets[category];
    let xml = sheet.xml;
    // A skipped employee must not retain the original file's prior input values in totals.
    for (const target of template.employees.filter(row => row.category === category && bindings[row.key] === '__skip__')) {
      const fields = target.attendanceOnly ? LABOR_FIELDS[category].filter(field => field.key === 'attendanceDays') : LABOR_FIELDS[category];
      for (const field of fields) {
        const ref = `${field.column}${target.row + (field.rowOffset ?? 0)}`;
        xml = replaceCellValue(xml, ref, null);
        clearedCells.push(`${CATEGORY_LABELS[category]}!${ref}`);
      }
    }
    for (const transfer of transfers.filter(t => t.category === category)) {
      const cell = getCell(sheet.document, transfer.cell);
      if (!cell || elements(cell, 'f').length) throw new Error(`入力セル ${transfer.cell} の再確認が必要です。`);
      xml = replaceCellValue(xml, transfer.cell, transfer.value);
    }
    // Update cached totals while preserving every formula, including hidden calculation columns.
    xml = recalculateWorksheet(xml, template.sharedStrings);
    calculatedSheets[category] = xml;
    // Formula totals in time columns need the same minute precision as their inputs.
    // Integer totals and all non-time columns retain the original display format.
    const timeColumns = new Set(LABOR_FIELDS[category].filter(field => field.unit === 'minutes').map(field => field.column));
    const sharedColumns = new Set(LABOR_FIELDS[category].filter(field => field.unit !== 'minutes' && timeColumns.has(field.column)).map(field => field.column));
    const timeInputs = new Set(template.employees.filter(target => target.category === category).flatMap(target =>
      LABOR_FIELDS[category].filter(field => field.unit === 'minutes').map(field => `${field.column}${target.row + (field.rowOffset ?? 0)}`)));
    for (const cell of elements(parseXml(xml), 'c')) {
      const ref = cell.getAttribute('r') ?? '';
      const formula = elements(cell, 'f')[0]?.textContent;
      const column = ref.replace(/\d+$/, '');
      if (!formula || !timeColumns.has(column)) continue;
      // D contains separate day and hour rows. Only style a shared-column total
      // when every referenced cell is a known time input in this template.
      if (sharedColumns.has(column)) {
        const references = formula.replace(/\$/g, '').toUpperCase().match(/[A-Z]+\d+(?::[A-Z]+\d+)?/g) ?? [];
        const expanded = references.flatMap(reference => {
          const range = reference.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/);
          if (!range) return [reference];
          if (range[1] !== range[3] || Number(range[4]) < Number(range[2]) || Number(range[4]) - Number(range[2]) > 10000) return [reference];
          return Array.from({ length: Number(range[4]) - Number(range[2]) + 1 }, (_, index) => `${range[1]}${Number(range[2]) + index}`);
        });
        if (!expanded.length || expanded.some(reference => !timeInputs.has(reference))) continue;
      }
      const value = Number(elements(cell, 'v')[0]?.textContent);
      if (Number.isFinite(value) && Math.abs(value - Math.round(value)) > 1e-9) fractional.push({ category, cell: ref });
    }
  }
  const styleIds = fractional.map(t => Number(getCell(template.sheets[t.category].document, t.cell)?.getAttribute('s') ?? 0));
  const styleFile = zip.file('xl/styles.xml');
  if (!styleFile) throw new Error('Excelの表示書式がありません。');
  const styles = fractionalStyles(await styleFile.async('string'), styleIds);
  if (fractional.length) zip.file('xl/styles.xml', styles.xml);
  for (const category of Object.keys(CATEGORY_LABELS) as LaborCategory[]) {
    let xml = calculatedSheets[category];
    for (const target of fractional.filter(t => t.category === category)) {
      const styleId = Number(getCell(template.sheets[category].document, target.cell)?.getAttribute('s') ?? 0);
      xml = replaceCellStyle(xml, target.cell, styles.ids.get(styleId)!);
    }
    zip.file(template.sheets[category].path, xml);
  }
  const workbookFile = zip.file('xl/workbook.xml')!;
  const workbook = await workbookFile.async('string');
  const calc = '<calcPr calcMode="auto" fullCalcOnLoad="1" forceFullCalc="1"/>';
  zip.file('xl/workbook.xml', /<calcPr\b[^>]*\/>/.test(workbook) ? workbook.replace(/<calcPr\b([^>]*)\/>/, (_all, attrs: string) => `<calcPr${attrs.replace(/\s+(?:calcMode|fullCalcOnLoad|forceFullCalc)="[^"]*"/g, '')} calcMode="auto" fullCalcOnLoad="1" forceFullCalc="1"/>`) : workbook.replace('</workbook>', `${calc}</workbook>`));
  return { bytes: await zip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' }), transfers,
    fractionalCells: fractional.map(t => `${CATEGORY_LABELS[t.category]}!${t.cell}`), clearedCells };
}
